import React from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
	bsSize?: string
	backdrop?: 'static' | boolean
	scrolable?: boolean
	children: React.ReactNode[]
}

interface ModalHeaderProps {
	title?: string
	children?: React.ReactNode
	hideCloseButton?: boolean
	closeModal?: () => void
}

interface ModalChildrenProps {
	children: React.ReactNode
}

function ModalHeader(props: ModalHeaderProps) {
	return (
		<div className="modal-header">
			{!props.children && (
				<>
					{!!props.title && (
						<h1 className="modal-title fs-5">{props.title}</h1>
					)}

					{!props.hideCloseButton && (
						<button
							type="button"
							className="ms-auto btn btn-sm fas fa-times"
							data-bs-dismiss="modal"
							aria-label="Close"
							onClick={() => props.closeModal?.()}
						/>
					)}
				</>
			)}
			{!!props.children && props.children}
		</div>
	);
}

function ModalBody(props: ModalChildrenProps) {
	return (
		<div className="modal-body">
			{props.children}
		</div>
	);
}

function ModalFooter(props: ModalChildrenProps) {
	return (
		<div className="modal-footer">
			{props.children}
		</div>
	);
}

const ModalRoot = React.forwardRef<any, ModalProps>(({ children, scrolable, bsSize, ...props }, ref) => {
	const [isOpen, setIsOpen] = React.useState(false);

	const closeModal = React.useCallback(() => {
		setIsOpen(false);
		document.getElementById('modal-backdrop')?.remove();

		document.body.style.overflow = '';
		document.body.style.paddingRight = '';
	}, []);

	const openModal = React.useCallback(() => {
		setIsOpen(true);
		const el = document.createElement('div');
		el.id = 'modal-backdrop';
		el.classList.add('modal-backdrop', 'fade', 'show');
		el.addEventListener('click', closeModal);
		document.body.appendChild(el);

		document.body.style.overflow = 'hidden';
		document.body.style.paddingRight = '15px';
	}, [closeModal]);

	React.useImperativeHandle(ref, () => ({
		open: openModal,
		close: closeModal,
	}));

	return createPortal(
		<div {...props} className={'modal fade' + `${isOpen ? ' show' : ''}`} tabIndex={-1} data-bs-backdrop={props.backdrop} style={{ display: isOpen ? 'block' : 'none' }}>
			<div className={`modal-dialog ${scrolable ? 'modal-dialog-scrollable' : ''} ${bsSize ? 'modal-' + bsSize : ''}`} >
				<div className="modal-content">
					{React.Children.map(
						children,
						(child) => !React.isValidElement(child) ? child : React.cloneElement(child, { ...child.props, closeModal })
					)}
				</div>
			</div>
		</div>,
		document.body
	);
});

ModalRoot.displayName = 'Modal';

const Modal = Object.assign(ModalRoot, {
	Header: ModalHeader,
	Body: ModalBody,
	Footer: ModalFooter,
});

export default Modal;
